import React, { useEffect, useState } from 'react';

import { RollbackOutlined } from '@ant-design/icons';
import { changePasswordByEmailFinish } from '@api/FW/changePassword/changePasswordByEmail';
import { notificationService } from '@app/services/notification.service';
import { Alert, Button, Flex, Form, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AuthFormWrap, AuthWrap } from './AuthWrap.styled';

type FormData = {
	password: string;
	password2: string;
};

const ChangePasswordForm = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams();
	const [loading, setLoading] = useState(false);
	const [password, setPassword] = useState<string | undefined>();
	const [password2, setPassword2] = useState<string | undefined>();
	const [disableButton, setDisableButton] = useState(false);
	const [requestSuccessful, setRequestSuccessful] = useState(false);

	useEffect(() => {
		if (password && password2) {
			setDisableButton(false);
		} else {
			setDisableButton(true);
		}
	}, [password, password2]);

	const submitForm = (values: FormData) => {
		setLoading(true);

		const userID = params.userID || '';

		changePasswordByEmailFinish(values.password, userID)
			.then(() => {
				setRequestSuccessful(true);
			})
			.catch((err) => {
				notificationService.error({
					message: err.message ? err.message : t('api.error'),
				});
			})
			.finally(() => setLoading(false));
	};

	return (
		<AuthWrap className='text-green'>
			{requestSuccessful ? (
				<>
					<h3>{t('passwordRecovery.changePasswordTitle')}</h3>
					<Typography.Title level={4} style={{ marginBottom: 10, padding: '0 10%' }}>
						{t('passwordRecovery.changePasswordSuccess')}
					</Typography.Title>
				</>
			) : (
				<>
					<h3>{t('passwordRecovery.changePasswordTitle')}</h3>

					<AuthFormWrap>
						<Alert
							className='mb-20'
							type='warning'
							message={t('passwordRecovery.changePasswordValidation')}
						/>
						<Form layout='vertical' onFinish={submitForm} size='large'>
							<Flex vertical gap={16}>
								<Form.Item<FormData>
									name='password'
									rules={[
										{ required: true, message: t('auth.passwordError') },
										{
											pattern: /(?=.*[A-Z])(?=.*\d).{6,}/,
											message: t('passwordRecovery.changePasswordValidation'),
										},
									]}>
									<Input.Password
										placeholder={t('passwordRecovery.changePasswordNewPswd')}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
									/>
								</Form.Item>

								<Form.Item<FormData>
									name='password2'
									rules={[
										{ required: true, message: t('auth.passwordError') },
										({ getFieldValue }) => ({
											validator(_, value) {
												if (!value || getFieldValue('password') === value) {
													return Promise.resolve();
												}
												return Promise.reject(new Error(t('passwordRecovery.changePasswordError')));
											},
										}),
									]}>
									<Input.Password
										placeholder={t('passwordRecovery.changePasswordRepeat')}
										value={password2}
										onChange={(e) => setPassword2(e.target.value)}
									/>
								</Form.Item>

								<Form.Item>
									<Button
										type='primary'
										htmlType='submit'
										loading={loading}
										disabled={disableButton}>
										{t('passwordRecovery.passwordRecoverySend')}
									</Button>
								</Form.Item>
							</Flex>
						</Form>
					</AuthFormWrap>
				</>
			)}

			<Typography.Link onClick={() => navigate('/auth/login')}>
				<RollbackOutlined /> {t('passwordRecovery.passwordRecoveryReturn')}
			</Typography.Link>
		</AuthWrap>
	);
};

export default ChangePasswordForm;
